import type { NodeConfig, NodeExecutedEvent } from './types'
import { isTextAgentChannel } from './types'

export type OutcomeTone = 'failure' | 'warning' | 'neutral'

// Branch/status labels that read as a hard failure vs an expected non-success.
const FAILURE_RE = /\b(error|fail|failed|failure|rejected|invalid|unreachable)\b/i
const WARNING_RE =
  /\b(no[\s_-]?answer|busy|timeout|timed[\s_-]?out|voicemail|cancel(?:led)?|declined|not[\s_-]?found)\b/i

// Chat-only branch vocabulary. Kept out of the shared patterns above so voice
// classification stays byte-identical: a voice path named "no reply" is
// classified exactly as it is today. Anchored, because exit names are authored by
// the flow designer — only the exit that IS "no reply" is a warning, while an
// intent merely containing the words ("No response needed") is a normal outcome.
const CHAT_WARNING_RE = /^\s*no[\s_-]?(?:reply|response)\s*$/i

/**
 * Classify a chosen-path / status string into a display tone. Defaults to neutral.
 * `channel` opts a node into channel-specific vocabulary; omit it for voice.
 */
export const getLabelTone = (label?: string | null, channel?: string | null): OutcomeTone => {
  if (!label) return 'neutral'
  if (FAILURE_RE.test(label)) return 'failure'
  if (WARNING_RE.test(label)) return 'warning'
  if (isTextAgentChannel(channel) && CHAT_WARNING_RE.test(label)) return 'warning'
  return 'neutral'
}

export type NodeOutcome = 'success' | 'failure' | 'warning'

/**
 * Per-node execution outcome, shown on every node (like the legacy debug logs,
 * which colour-coded every card). Failure/warning is derived from the node's own
 * result — HTTP status, or a tool/skill execution status that reads as error/warn.
 * Everything else is treated as success. Branch outcomes (No Answer / Error path)
 * are conveyed separately by the chosen-path pill.
 */
export const getNodeOutcome = (node: NodeExecutedEvent): NodeOutcome => {
  const cfg = node.node_config as NodeConfig | undefined
  if (!cfg) return 'success'

  const status = Number(cfg.status_code)
  if (cfg.status_code != null && !Number.isNaN(status)) {
    if (status >= 400) return 'failure'
    if (status >= 300) return 'warning'
    return 'success'
  }

  const tone = getLabelTone(cfg.execution_status || cfg.skill_status)
  if (tone === 'failure') return 'failure'
  if (tone === 'warning') return 'warning'

  // Chat only: a handoff node reports its result in `assignment_status`, which
  // nothing here used to read — so a rejected handoff still showed "Success" while
  // the customer was never queued. `in_queue` classifies neutral and stays a
  // success; the eventual branch is conveyed by the chosen-path pill as usual.
  // Gated because phlo-core writes this field on every channel: voice and WhatsApp
  // queue cards must classify exactly as they do today; SMS shares chat's reading.
  // These statuses are snake_case (`enqueue_failed`), and `_` is a word character,
  // so the shared patterns' \b never matches inside one — hence the split on it.
  if (isTextAgentChannel(cfg.channel) && cfg.assignment_status) {
    const assignmentTone = getLabelTone(cfg.assignment_status.replace(/_/g, ' '), cfg.channel)
    if (assignmentTone === 'failure') return 'failure'
    if (assignmentTone === 'warning') return 'warning'
  }

  return 'success'
}
